import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Observable, of, isObservable } from 'rxjs';
import { FieldDictionaryListService } from './field-dictionary-list.service';
import { TranslateService } from '@ngx-translate/core';
import { SqlSearchService } from '../sql-search/sql-search.service';
import { FieldDictionaryDto } from '../../models/base/base';


@Injectable({
  providedIn: 'root'
})
export class FieldDicHelperService {
  constructor(
    private fieldDictionaryListService: FieldDictionaryListService,
    private sqlExquationService: SqlSearchService,
    private translate: TranslateService
  ) { }

  /**
   * تحميل بيانات الفيلد من FieldDictionaryListService
   */
  getFieldData(
    fieldName: string,
    systemMenuId: string,
    validate: boolean,
    control?: FormControl,
    currentLang?: string
  ): Observable<{ placeholder: string; defaultValue?: any; type?: string; disabled?: boolean }> {
    // Guard: Return default if no systemMenuId
    if (!systemMenuId) {
      return of({
        placeholder: this.getDefaultPlaceholder(fieldName),
        defaultValue: undefined,
        type: undefined,
        disabled: false
      });
    }

    const list$ = this.fieldDictionaryListService.getList();

    // Guard: Ensure getList() returns an Observable
    if (!list$ || !isObservable(list$)) {
      return of({
        placeholder: this.getDefaultPlaceholder(fieldName),
        defaultValue: undefined,
        type: undefined,
        disabled: false
      });
    }

    return list$.pipe(
      map((res) => {
        const pageData = res?.find((x) => x.pageid == systemMenuId);
        if (!pageData) {
          return { placeholder: this.getDefaultPlaceholder(fieldName), type: undefined, disabled: false };
        }

        const pageDataList = pageData.list || [];

        const fieldList = pageDataList.find(
          (x) => x.fieldname?.toLowerCase() === fieldName?.toLowerCase()
        );

        return {
          fieldList,
          placeholder: this.getTranslatedPlaceholder(fieldList, fieldName, currentLang),
        };
      }),
      switchMap(({ fieldList, placeholder }: any) => {
        if (!validate || !fieldList) {
          return of({ placeholder }); // 👈 بيرجع placeholder بس
        }

        if (fieldList && fieldList.autoincrement) {
          const nextSerial$ = this.getNextSerialValue(fieldList, control);

          // Guard: Ensure getNextSerialValue returns an Observable
          if (!nextSerial$ || !isObservable(nextSerial$)) {
            return of({
              placeholder,
              defaultValue: fieldList?.defaultvalue,
              type: undefined,
              disabled: fieldList.enable ? false : true,
            });
          }

          return nextSerial$.pipe(
            tap(() => this.setDefaultValue(fieldList, control)),
            map(({ value, type }) => ({
              placeholder,
              defaultValue: value ?? fieldList?.defaultvalue,
              type,
              disabled: fieldList.enable ? false : true,
            })),
            catchError((err) => {
              return of({
                placeholder,
                defaultValue: fieldList?.defaultvalue, // fallback على الـ defaultvalue
                type: undefined,
                disabled: fieldList.enable ? false : true,
              });
            })
          );
        }

        return of({
          placeholder,
          defaultValue: fieldList?.defaultvalue ?? control?.value, // 👈 رجع الـ defaultvalue هنا كمان
          type: undefined,
          disabled: fieldList.enable ? false : true,
        });
      }),
      catchError((err) => {
        return of({
          placeholder: this.getDefaultPlaceholder(fieldName),
          defaultValue: undefined,
          type: undefined,
          disabled: false
        });
      })
    );
  }


  private getDefaultPlaceholder(fieldName: string): string {
    return fieldName.toLowerCase() === 'search'
      ? this.translate.instant(fieldName)
      : this.translate.instant('enter') + ' ' + this.translate.instant(fieldName);
  }

  private getTranslatedPlaceholder(fieldList: any, fieldName: string, currentLang?: string): string {
    if (!fieldList) return this.getDefaultPlaceholder(fieldName);

    const fieldNameTranslation =
      currentLang === 'ar'
        ? fieldList?.langvalue.find((x) => x.culturename == 'ar-EG')?.value
        : fieldList?.langvalue.find((x) => x.culturename == currentLang)?.value;

    return this.translate.instant('enter') + ' ' + fieldNameTranslation;
  }

  private setDefaultValue(fieldList: FieldDictionaryDto, control?: FormControl): void {
    if (fieldList?.defaultvalue != null) {
      control?.setValue(fieldList.defaultvalue, { emitEvent: false });
    }
  }

  private getNextSerialValue(
    fieldList: FieldDictionaryDto,
    control?: FormControl,
    date: any = null,
  ): Observable<{ value: string; type?: string }> {
    if (!fieldList?.autoincrement) return of({ value: null });

    const tableName = fieldList.menukey;
    const fieldName = fieldList.fieldname;
    const pageParameters = fieldList.parameter;

    // Guard: Check required parameters
    if (!tableName || !fieldName) {
      return of({ value: fieldList?.defaultvalue ?? '', type: undefined });
    }

    let type: string | undefined;
    if (fieldList.autoincrement > 25) {
      type = 'text';
    }

    try {
      const serial$ = this.sqlExquationService.getMaxSequenceSerial(tableName, fieldName, pageParameters, date, false);

      // Guard: Ensure the service method returns an Observable
      if (!serial$ || !isObservable(serial$)) {
        return of({ value: fieldList?.defaultvalue ?? '', type });
      }

      return serial$.pipe(
        map((res) => {
          const finalValue = res?.result ?? fieldList?.defaultvalue ?? '';
          control?.setValue(finalValue, { emitEvent: false });
          return { value: String(finalValue), type };
        }),
        catchError((err) => {
          const fallback = fieldList?.defaultvalue ?? '';
          control?.setValue(fallback, { emitEvent: false });
          return of({ value: String(fallback), type });
        })
      );
    } catch (err) {
      return of({ value: fieldList?.defaultvalue ?? '', type });
    }
  }



  private getEnableProperty(fieldList: FieldDictionaryDto, control?: FormControl): void {
    if (!fieldList?.enable && fieldList?.fieldname) {
      // debugger
      control?.disable({ emitEvent: false });
    }
  }
}

